// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

/**
 * What a bid package is actually being compared against.
 *
 * A trade scope used to carry only `divisionIds`, so its budget was always
 * whole CSI divisions. That is right for Division 03, where one sub bids all
 * the concrete. It is wrong for most of the rest.
 *
 * On the sandbox project, the Framing scope mapped to Division 06, Wood,
 * Plastics & Composites:
 *
 *     06-100  Rough Carpentry    $411,000
 *     06-200  Finish Carpentry   $647,000
 *
 * The framing subs bid $432,000 -- slightly OVER the rough carpentry they
 * were actually bidding. The tab reported 53% UNDER BUDGET, because it was
 * comparing a framing bid against framing plus all the millwork, casework and
 * trim. A flattering, confident, wrong number in the largest type on the page.
 *
 * Division 09 is the same story four times over: drywall, painting, flooring
 * and tile are four packages, four subcontractors, and one budget figure.
 *
 * So a scope may name SUBCATEGORIES instead. They already carry the cost
 * codes a package is let under -- 06-100, 13-05 -- which is exactly the
 * granularity being bid. Divisions remain the fallback, so every project that
 * existed before this keeps working untouched.
 */

import { walkItems, getItemTotal, inBaseBid } from "./calculations";

/** The part of a BidScope this module needs. */
export interface ScopeLike {
  divisionIds?: string[];
  /** Subcategory ids. When present these decide the budget, not the divisions. */
  subIds?: string[];
}

/** True when this scope has been narrowed below whole divisions. */
export const isNarrowed = (scope: ScopeLike | null | undefined): boolean =>
  !!scope?.subIds && scope.subIds.length > 0;

/** Every subcategory in the project, with the division it belongs to. */
export function allSubcategories(divisions: any[] | null | undefined): Array<{
  id: string; name: string; costCode: string; divId: string; divCode: string; divName: string;
}> {
  const out: Array<{ id: string; name: string; costCode: string; divId: string; divCode: string; divName: string }> = [];
  for (const div of divisions || []) {
    for (const sub of div?.subcategories || []) {
      out.push({
        id: sub.id, name: sub.name ?? "", costCode: sub.costCode ?? "",
        divId: div.id, divCode: div.code ?? "", divName: div.name ?? "",
      });
    }
  }
  return out;
}

/**
 * The priced work inside a scope.
 *
 * Uses inBaseBid rather than restating the rule, because "what counts toward
 * a total" is written in one place in this codebase and has been copied
 * enough times already. Excluded lines, clarifications and un-accepted
 * alternates are out, exactly as they are everywhere else.
 */
export function scopeLineItems(
  scope: ScopeLike | null | undefined,
  divisions: any[] | null | undefined,
  itemState: Record<string, any> | null | undefined,
): Array<{ itemId: string; desc: string; amt: number; divId: string; divName: string; subId: string; subName: string }> {
  const st = itemState || {};
  const narrowed = isNarrowed(scope);
  const subs = new Set(scope?.subIds || []);
  const divs = new Set(scope?.divisionIds || []);
  const rows: Array<{ itemId: string; desc: string; amt: number; divId: string; divName: string; subId: string; subName: string }> = [];

  for (const div of divisions || []) {
    if (!narrowed && !divs.has(div.id)) continue;
    for (const sub of div?.subcategories || []) {
      if (narrowed && !subs.has(sub.id)) continue;
      for (const item of walkItems(sub?.rows || [])) {
        const li = st[(item as any).id] || {};
        if (!inBaseBid(li)) continue;
        const amt = getItemTotal(li, st);
        if (!amt) continue;
        rows.push({
          itemId: (item as any).id,
          desc: li.description || (item as any).description || "",
          amt, divId: div.id, divName: div.name ?? "",
          subId: sub.id, subName: sub.name ?? "",
        });
      }
    }
  }
  return rows;
}

/** What this scope's work adds up to. */
export const scopeBudget = (
  scope: ScopeLike | null | undefined,
  divisions: any[] | null | undefined,
  itemState: Record<string, any> | null | undefined,
): number => scopeLineItems(scope, divisions, itemState).reduce((a, r) => a + r.amt, 0);

/**
 * How a scope describes its own coverage, for the one line under its name.
 *
 * Says which cost codes rather than how many, because "2 subcategories" tells
 * an estimator nothing and "06-100, 06-200" tells them whether the comparison
 * they are looking at is the one they meant.
 */
export function scopeCoverage(
  scope: ScopeLike | null | undefined,
  divisions: any[] | null | undefined,
): string {
  const all = allSubcategories(divisions);
  if (isNarrowed(scope)) {
    const picked = all.filter(s => scope!.subIds!.includes(s.id));
    if (!picked.length) return "nothing selected";
    const codes = picked.map(s => s.costCode || s.name).filter(Boolean);
    return codes.join(", ");
  }
  const divs = (divisions || []).filter(d => (scope?.divisionIds || []).includes(d.id));
  if (!divs.length) return "nothing selected";
  return divs.map(d => `all of ${d.code || d.name}`).join(", ");
}

/**
 * Find the subcategory a cost code refers to.
 *
 * BuildingConnected names a package with its cost code in front -- "13-05:
 * Pool , Spa and Interior Diamond Spa" -- and subcategories carry the same
 * codes, so an imported package can land on the right budget instead of on
 * no budget at all.
 *
 * Exact on the code and nothing else. A near match would silently point a
 * package at the wrong trade's money, which is the failure this whole module
 * exists to stop.
 */
export function matchSubByCostCode(
  text: string, divisions: any[] | null | undefined,
): { id: string; divId: string } | null {
  const code = (text || "").trim().match(/^\s*([0-9]{2}[-–][0-9]{2,4})/)?.[1]?.replace("–", "-");
  if (!code) return null;
  const hit = allSubcategories(divisions).find(s => s.costCode.trim() === code);
  return hit ? { id: hit.id, divId: hit.divId } : null;
}
